/**
 * EmailConfigDialog (Phase 7, §API/§Frontend) — per-account IMAP "Kontowecker"
 * mailbox config. Create/edit mirrors `ContractFormDialog.tsx`'s mechanics
 * (Modal + `Field` render-prop + Input/Select, the seedKey re-seed-during-
 * render idiom, inline imperative validation via `setFormError`, delete behind
 * a nested `ConfirmDialog`).
 *
 * The config is a 1:1 sidecar per account (`GET .../email-config` 404s
 * `not_found` until one exists) — that 404 is a normal "create mode" signal,
 * not an error state (§`useEmailConfig`). Password is write-only end-to-end:
 * once a config exists (`has_password`), the field shows the placeholder
 * "•••• gespeichert" and stays empty; only a non-empty edit is sent in the
 * PUT body (`password: null` otherwise keeps the stored ciphertext, exactly
 * like the backend's own upsert contract).
 *
 * "Verbindung testen" / "Jetzt abrufen" and the status block / "Nachrichten
 * anzeigen" entry only make sense against an already-stored config (the
 * backend endpoints 404 without one), so they only render once the config
 * exists — i.e. after the first save.
 */
import { useState } from 'react';
import { Modal } from '../../components/ui/Modal.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { Input } from '../../components/ui/Input.tsx';
import { Field } from '../../components/ui/Field.tsx';
import { Select, type SelectOption } from '../../components/ui/Select.tsx';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.tsx';
import { SkeletonList } from '../../components/ui/Skeleton.tsx';
import { ErrorState } from '../../components/ui/ErrorState.tsx';
import { useToast } from '../../context/ToastContext.tsx';
import { ApiRequestError } from '../../lib/api.ts';
import { formatDateTime } from '../../lib/format.ts';
import {
  useDeleteEmailConfig,
  useEmailConfig,
  useFetchNowEmailConfig,
  useTestEmailConfig,
  useUpsertEmailConfig,
} from '../../lib/hooks/useEmailConfig.ts';
import type { AccountOut, EmailIngestConfigIn, EmailIngestConfigOut } from '../../lib/types.ts';
import { EmailMessagesDialog } from './EmailMessagesDialog.tsx';

const PARSER_OPTIONS: SelectOption[] = [{ value: 'haspa_kontowecker', label: 'Haspa Kontowecker' }];

const PASSWORD_PLACEHOLDER = '•••• gespeichert';

interface Draft {
  host: string;
  port: string;
  useSsl: boolean;
  username: string;
  password: string;
  folder: string;
  parser: string;
  active: boolean;
}

function buildDraft(config?: EmailIngestConfigOut | null): Draft {
  if (config) {
    return {
      host: config.imap_host,
      port: String(config.imap_port),
      useSsl: config.use_ssl,
      username: config.username,
      password: '',
      folder: config.folder,
      parser: config.parser,
      active: config.active,
    };
  }
  return {
    host: '',
    port: '993',
    useSsl: true,
    username: '',
    password: '',
    folder: 'INBOX',
    parser: 'haspa_kontowecker',
    active: true,
  };
}

export interface EmailConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: AccountOut | null;
}

export function EmailConfigDialog({ open, onOpenChange, account }: EmailConfigDialogProps) {
  if (!open || !account) return null;
  return <EmailConfigForm account={account} onClose={() => onOpenChange(false)} />;
}

function EmailConfigForm({ account, onClose }: { account: AccountOut; onClose: () => void }) {
  const { toast } = useToast();
  const configQuery = useEmailConfig(account.id);
  const upsert = useUpsertEmailConfig(account.id);
  const remove = useDeleteEmailConfig(account.id);
  const test = useTestEmailConfig(account.id);
  const fetchNow = useFetchNowEmailConfig(account.id);

  const notFound =
    configQuery.isError &&
    configQuery.error instanceof ApiRequestError &&
    configQuery.error.code === 'not_found';
  const config = notFound ? null : (configQuery.data ?? null);
  const isEdit = config !== null;

  const [draft, setDraft] = useState<Draft>(() => buildDraft(config));
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [messagesOpen, setMessagesOpen] = useState(false);

  // Re-seed once the config query settles into a definite shape, without an
  // effect (React docs pattern, mirrors ContractFormDialog's `seedKey`).
  const seedKey = configQuery.isPending ? 'loading' : isEdit ? `c${config.id}` : 'new';
  const [lastSeedKey, setLastSeedKey] = useState('loading');
  if (seedKey !== lastSeedKey) {
    setLastSeedKey(seedKey);
    if (seedKey !== 'loading') {
      setDraft(buildDraft(config));
      setFormError(null);
      setTestResult(null);
    }
  }

  const pending = upsert.isPending;

  function patch(next: Partial<Draft>) {
    setDraft((prev) => ({ ...prev, ...next }));
  }

  function submit() {
    const host = draft.host.trim();
    const username = draft.username.trim();
    const folder = draft.folder.trim() || 'INBOX';
    const port = Number.parseInt(draft.port, 10);
    const password = draft.password.trim();

    if (!host) {
      setFormError('Bitte einen IMAP-Host angeben.');
      return;
    }
    if (!username) {
      setFormError('Bitte einen Benutzernamen angeben.');
      return;
    }
    if (!Number.isFinite(port) || port < 1 || port > 65535) {
      setFormError('Bitte einen gültigen Port angeben.');
      return;
    }
    if (!isEdit && !password) {
      setFormError('Für eine neue Konfiguration ist ein Passwort erforderlich.');
      return;
    }
    setFormError(null);

    const body: EmailIngestConfigIn = {
      imap_host: host,
      imap_port: port,
      use_ssl: draft.useSsl,
      username,
      password: password || null,
      folder,
      parser: draft.parser,
      active: draft.active,
    };

    upsert.mutate(body, {
      onSuccess: () => {
        patch({ password: '' });
        toast('E-Mail-Konfiguration gespeichert.', { variant: 'success' });
      },
      onError: (err) => setFormError(err.message),
    });
  }

  function confirmDelete() {
    remove.mutate(undefined, {
      onSuccess: () => {
        setConfirmDeleteOpen(false);
        toast('E-Mail-Konfiguration gelöscht.', { variant: 'success' });
        onClose();
      },
      onError: (err) => {
        setConfirmDeleteOpen(false);
        toast(err.message, { variant: 'error' });
      },
    });
  }

  function runTest() {
    setTestResult(null);
    test.mutate(undefined, {
      onSuccess: (result) =>
        setTestResult({
          ok: result.ok,
          message: result.ok ? 'Verbindung erfolgreich.' : (result.error ?? 'Verbindung fehlgeschlagen.'),
        }),
      onError: (err) => setTestResult({ ok: false, message: err.message }),
    });
  }

  function runFetchNow() {
    fetchNow.mutate(undefined, {
      onSuccess: (result) => {
        if (result.status === 'error') {
          toast(result.error ?? 'Abruf fehlgeschlagen.', { variant: 'error' });
        } else if (result.status === 'locked') {
          toast('Abruf läuft bereits, bitte später erneut versuchen.', { variant: 'info' });
        } else {
          toast(
            `Abruf abgeschlossen: ${result.stored} neu, ${result.parsed} geparst, ${result.ignored} ignoriert, ${result.errored} fehlerhaft.`,
            { variant: 'success' },
          );
        }
      },
      onError: (err) => toast(err.message, { variant: 'error' }),
    });
  }

  return (
    <>
      <Modal
        open
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
        title="E-Mail-Import"
        description={account.name}
        size="lg"
        footer={
          <>
            {isEdit ? (
              <Button
                variant="danger"
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={pending || remove.isPending}
                className="mr-auto"
              >
                Löschen
              </Button>
            ) : null}
            <Button variant="secondary" onClick={onClose} disabled={pending}>
              Schließen
            </Button>
            <Button onClick={submit} disabled={pending || configQuery.isPending}>
              {pending ? 'Speichere …' : 'Speichern'}
            </Button>
          </>
        }
      >
        {configQuery.isPending ? (
          <SkeletonList rows={4} />
        ) : configQuery.isError && !notFound ? (
          <ErrorState error={configQuery.error} onRetry={() => void configQuery.refetch()} />
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Host" required>
                {(id) => (
                  <Input
                    id={id}
                    value={draft.host}
                    onChange={(e) => patch({ host: e.target.value })}
                    placeholder="mail.matzradloff.info"
                  />
                )}
              </Field>
              <Field label="Port" required>
                {(id) => (
                  <Input
                    id={id}
                    type="number"
                    value={draft.port}
                    onChange={(e) => patch({ port: e.target.value })}
                  />
                )}
              </Field>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Benutzer" required>
                {(id) => (
                  <Input
                    id={id}
                    value={draft.username}
                    onChange={(e) => patch({ username: e.target.value })}
                    autoComplete="off"
                  />
                )}
              </Field>
              <Field
                label="Passwort"
                required={!isEdit}
                hint={
                  isEdit
                    ? 'Leer lassen, um das gespeicherte Passwort zu behalten.'
                    : undefined
                }
              >
                {(id) => (
                  <Input
                    id={id}
                    type="password"
                    value={draft.password}
                    onChange={(e) => patch({ password: e.target.value })}
                    placeholder={isEdit && config.has_password ? PASSWORD_PLACEHOLDER : undefined}
                    autoComplete="new-password"
                  />
                )}
              </Field>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Ordner">
                {(id) => (
                  <Input
                    id={id}
                    value={draft.folder}
                    onChange={(e) => patch({ folder: e.target.value })}
                  />
                )}
              </Field>
              <Field label="Parser">
                {(id) => (
                  <Select
                    id={id}
                    value={draft.parser}
                    onValueChange={(v) => patch({ parser: v })}
                    options={PARSER_OPTIONS}
                  />
                )}
              </Field>
            </div>

            <div className="flex flex-wrap items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border border-slate-200 accent-sky-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 dark:border-slate-700 dark:accent-sky-500"
                  checked={draft.useSsl}
                  onChange={(e) => patch({ useSsl: e.target.checked })}
                />
                SSL
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border border-slate-200 accent-sky-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 dark:border-slate-700 dark:accent-sky-500"
                  checked={draft.active}
                  onChange={(e) => patch({ active: e.target.checked })}
                />
                Aktiv
              </label>
            </div>

            {formError ? <p className="text-sm text-rose-600 dark:text-rose-400">{formError}</p> : null}

            {isEdit ? (
              <div className="space-y-4 border-t border-slate-100 pt-4 dark:border-slate-800/70">
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={runTest}
                    disabled={test.isPending}
                  >
                    {test.isPending ? 'Teste …' : 'Verbindung testen'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={runFetchNow}
                    disabled={fetchNow.isPending}
                  >
                    {fetchNow.isPending ? 'Rufe ab …' : 'Jetzt abrufen'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setMessagesOpen(true)}>
                    Nachrichten anzeigen
                  </Button>
                </div>

                {testResult ? (
                  <p
                    className={
                      testResult.ok
                        ? 'text-sm text-emerald-600 dark:text-emerald-400'
                        : 'text-sm text-rose-600 dark:text-rose-400'
                    }
                  >
                    {testResult.message}
                  </p>
                ) : null}

                <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs text-slate-500 sm:grid-cols-2 dark:text-slate-400">
                  <div className="flex justify-between gap-2 sm:justify-start">
                    <dt className="font-medium text-slate-600 dark:text-slate-300">
                      Letzte Verbindung
                    </dt>
                    <dd>
                      {config.last_connected_at ? formatDateTime(config.last_connected_at) : '—'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-2 sm:justify-start">
                    <dt className="font-medium text-slate-600 dark:text-slate-300">
                      Letztes Ereignis
                    </dt>
                    <dd>{config.last_event_at ? formatDateTime(config.last_event_at) : '—'}</dd>
                  </div>
                  <div className="flex justify-between gap-2 sm:justify-start">
                    <dt className="font-medium text-slate-600 dark:text-slate-300">Fehleranzahl</dt>
                    <dd>{config.error_count}</dd>
                  </div>
                  <div className="flex justify-between gap-2 sm:col-span-2 sm:justify-start">
                    <dt className="font-medium text-slate-600 dark:text-slate-300">Letzter Fehler</dt>
                    <dd className="truncate">{config.last_error ?? '—'}</dd>
                  </div>
                </dl>
              </div>
            ) : null}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="E-Mail-Konfiguration löschen"
        message="E-Mail-Import für dieses Konto wirklich löschen? Gespeicherte Nachrichten gehen dabei verloren."
        confirmLabel="Löschen"
        danger
        loading={remove.isPending}
        onConfirm={confirmDelete}
      />

      {isEdit ? (
        <EmailMessagesDialog
          open={messagesOpen}
          onOpenChange={setMessagesOpen}
          accountId={account.id}
        />
      ) : null}
    </>
  );
}
